import React from "react";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Link from "@mui/material/Link";

const CalculatorQuestions = [
  {
    q_id: "0",
    q_title: "Monthly rent",
    q_description: (answers, descriptionParams) => {
      return (
        <>
          <Typography variant="body1" gutterBottom>
            What is the expected monthly rental income for this property?
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Current market rent for similar properties in the area
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Consider seasonal variations and vacancy rates
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Include any additional income (parking, storage, etc.)
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </>
      );
    },
    q_type: "currency",
    q_help_text: "Expected monthly rental income",
    q_placeholder: "Enter monthly rent",
    q_invalid_answer_help_text: "Please enter a valid amount",
    a_type: "currency",
    a_type_input: "text",
    a_string: "",
    a_error: false,
    q_validation_func: (value, answers) => {
      return !isNaN(value) && parseFloat(value) >= 0;
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    node_type: "node",
    node_visibility: "visible",
  },
  {
    q_id: "00",
    q_title: "Monthly mortgage",
    q_description: (answers, descriptionParams) => {
      return (
        <>
          <Typography variant="body1" gutterBottom>
            What is the monthly mortgage payment for this property?
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Principal and interest payment
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Does not include property taxes or insurance (entered
                    separately)
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Based on your loan amount, interest rate, and term
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </>
      );
    },
    q_type: "currency",
    q_help_text: "Monthly mortgage payment",
    q_placeholder: "Enter monthly mortgage",
    q_invalid_answer_help_text: "Please enter a valid amount",
    a_type: "currency",
    a_type_input: "text",
    a_string: "",
    a_error: false,
    q_validation_func: (value, answers) => {
      return !isNaN(value) && parseFloat(value) >= 0;
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    node_type: "node",
    node_visibility: "visible",
  },
  {
    q_id: "000",
    q_title: "Annual taxes",
    q_description: (answers, descriptionParams) => {
      return (
        <>
          <Typography variant="body1" gutterBottom>
            What are the annual property taxes for this property?
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Total annual property tax amount
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Can be found on property tax records or county assessor's
                    website
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Consider potential tax increases in your area
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </>
      );
    },
    q_type: "currency",
    q_help_text: "Annual property taxes",
    q_placeholder: "Enter annual taxes",
    q_invalid_answer_help_text: "Please enter a valid amount",
    a_type: "currency",
    a_type_input: "text",
    a_string: "",
    a_error: false,
    q_validation_func: (value, answers) => {
      return !isNaN(value) && parseFloat(value) >= 0;
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    node_type: "node",
    node_visibility: "visible",
  },
  {
    q_id: "0000",
    q_title: "Annual insurance",
    q_description: (answers, descriptionParams) => {
      return (
        <>
          <Typography variant="body1" gutterBottom>
            What is the annual property insurance cost for this property?
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Annual homeowner's insurance premium
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Include flood insurance if required
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Consider coverage amount and deductible
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </>
      );
    },
    q_type: "currency",
    q_help_text: "Annual property insurance",
    q_placeholder: "Enter annual insurance",
    q_invalid_answer_help_text: "Please enter a valid amount",
    a_type: "currency",
    a_type_input: "text",
    a_string: "",
    a_error: false,
    q_validation_func: (value, answers) => {
      return !isNaN(value) && parseFloat(value) >= 0;
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    node_type: "node",
    node_visibility: "visible",
  },
  {
    q_id: "00000",
    q_title: "Monthly reserve",
    q_description: (answers, descriptionParams) => {
      return (
        <>
          <Typography variant="body1" gutterBottom>
            What amount would you like to set aside monthly for maintenance and
            repairs?
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • General maintenance and repairs
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Property age and condition may affect this amount
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Common rule: 1-2% of property value annually
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </>
      );
    },
    q_type: "currency",
    q_help_text: "Monthly reserve for maintenance and repairs",
    q_placeholder: "Enter monthly reserve",
    q_invalid_answer_help_text: "Please enter a valid amount",
    a_type: "currency",
    a_type_input: "text",
    a_string: "",
    a_error: false,
    q_validation_func: (value, answers) => {
      return !isNaN(value) && parseFloat(value) >= 0;
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    node_type: "node",
    node_visibility: "visible",
  },
  {
    q_id: "000000",
    q_title: "Term length",
    q_description: (answers, descriptionParams) => {
      return (
        <>
          <Typography variant="body1" gutterBottom>
            How many years would you like to calculate the IRR for?
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Investment holding period in years
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Maximum 30 years (typical mortgage term)
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Consider your investment strategy and exit plan
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </>
      );
    },
    q_type: "number",
    q_help_text: "Number of years to calculate IRR",
    q_placeholder: "Enter term length in years",
    q_invalid_answer_help_text: "Please enter a valid number of years",
    a_type: "number",
    a_type_input: "number",
    a_string: "",
    a_error: false,
    q_validation_func: (value, answers) => {
      return !isNaN(value) && parseFloat(value) > 0 && parseFloat(value) <= 30;
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    node_type: "node",
    node_visibility: "visible",
  },
  {
    q_id: "0000000",
    q_title: "Downpayment",
    q_description: (answers, descriptionParams) => {
      return (
        <>
          <Typography variant="body1" gutterBottom>
            What is the expected or actual cash downpayment for this property?
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Initial cash investment required
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Typically 20-25% of purchase price for investment
                    properties
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal >= 0;
    },
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "00000000",
    q_title: "Closing costs",
    q_description: (answers, descriptionParams) => {
      return (
        <>
          <Typography variant="body1" gutterBottom>
            What are the expected or actual closing costs for this transaction?
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Total closing costs including:
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Loan origination fees
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Title insurance and fees
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Appraisal and inspection fees
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Typically 2-5% of purchase price
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal >= 0;
    },
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "000000000",
    q_title: "Holding period",
    q_description: (answers, descriptionParams) => {
      return (
        <>
          <Typography variant="body1" gutterBottom>
            How many years are you planning on holding this property?
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    Expected investment duration
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    Usually matches your financing term
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </>
      );
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal > 0;
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "0000000000",
    q_title: "Calculation name",
    q_description: (answers, descriptionParams) => {
      return (
        <>
          <Typography variant="body1" gutterBottom>
            What would you like to name this calculation?
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Use a descriptive name to identify this analysis
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Example: 'IRR Calc for 123 Main St'
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Helps organize multiple calculations
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </>
      );
    },
    q_type: "text",
    q_help_text: "Name this calculation",
    q_placeholder: "Enter calculation name",
    q_invalid_answer_help_text: "Please enter a name",
    a_type: "text",
    a_type_input: "text",
    a_string: "",
    a_error: false,
    q_validation_func: (value, answers) => {
      return value.length > 0;
    },
    q_next_step_func: (answer, answers, questions) => {
      return answer.q_id + "0";
    },
    node_type: "node",
    node_visibility: "visible",
  },
  {
    q_id: "00000000000",
    q_title: "Address (optional)",
    q_description: (answers, descriptionParams) => {
      return (
        <>
          <Typography variant="body1" gutterBottom>
            Enter property address to help organize your calculations
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Full address, city, state, or zip code
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Optional field for organization purposes
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    • Helps track multiple properties
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </>
      );
    },
    q_type: "address",
    q_help_text: "Property address",
    q_placeholder: "Enter property address",
    q_invalid_answer_help_text: "Please enter a valid address",
    a_type: "address",
    a_type_input: "text",
    a_string: "",
    a_error: false,
    q_validation_func: (value, answers) => {
      return true; // Address is optional
    },
    q_next_step_func: (answer, answers, questions) => {
      return null;
    },
    node_type: "leaf",
    node_visibility: "visible",
  },
];

export default CalculatorQuestions;
